import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 4;
const categories = ["全部", "最新动态", "娱乐新闻", "活动预告"];

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const categoryParam = searchParams.get("category") ?? "全部";
  const sort = searchParams.get("sort") ?? "latest";
  const page = Number(searchParams.get("page") ?? "1");

  usePageMeta({
    title: "大岛优香最新资讯 - 明星新闻频道",
    description:
      "大岛优香最新资讯频道，实时更新她的最新动态、娱乐新闻与活动预告，第一时间了解她的每一步，内容持续更新。",
    keywords: "大岛优香最新资讯,明星新闻,娱乐新闻,活动预告,最新动态",
  });

  const [draftKeyword, setDraftKeyword] = useState(keyword);

  const filtered = useMemo(() => {
    let list = [...newsList];
    if (categoryParam && categoryParam !== "全部") {
      list = list.filter((n) => n.category === categoryParam);
    }
    if (keyword.trim()) {
      const k = keyword.trim().toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(k) ||
          n.summary.toLowerCase().includes(k) ||
          n.category.toLowerCase().includes(k)
      );
    }
    list.sort((a, b) =>
      sort === "hot" ? b.views - a.views : b.date.localeCompare(a.date)
    );
    return list;
  }, [categoryParam, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateParam("q", draftKeyword.trim());
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">明星资讯</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
          {categoryParam === "全部" ? "明星资讯" : categoryParam}
        </h1>
        <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
          汇集大岛优香的最新动态、娱乐新闻与活动预告，支持按分类筛选、按时间或热度排序，帮助你快速定位感兴趣的内容。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 p-4 bg-background-100 border border-background-200 rounded-lg">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => updateParam("category", cat === "全部" ? "" : cat)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    categoryParam === cat
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-xs text-foreground-500 mr-1">排序</span>
              {[
                { key: "latest", label: "最新" },
                { key: "hot", label: "最热" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => updateParam("sort", opt.key)}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSearch} className="mb-6 flex flex-col sm:flex-row gap-2">
            <div className="flex items-center flex-1 bg-background-50 border border-background-200 rounded-md px-3 py-2 focus-within:border-primary-300 transition-colors">
              <i className="ri-search-line text-foreground-500 w-4 h-4 flex items-center justify-center text-sm" />
              <input
                type="text"
                name="q"
                value={draftKeyword}
                onChange={(e) => setDraftKeyword(e.target.value)}
                placeholder="输入关键词搜索资讯"
                className="bg-transparent border-0 outline-none text-sm text-foreground-800 placeholder:text-foreground-400 flex-1 px-2"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          {keyword && (
            <p className="text-sm text-foreground-600 mb-4">
              「<span className="text-primary-600 font-medium">{keyword}</span>」共找到{" "}
              {filtered.length} 条结果
            </p>
          )}

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {paged.map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => updateParam("page", String(currentPage - 1))}
                    className="w-9 h-9 rounded-md border border-background-200 flex items-center justify-center text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => updateParam("page", String(p))}
                      className={`w-9 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                        p === currentPage
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-200 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => updateParam("page", String(currentPage + 1))}
                    className="w-9 h-9 rounded-md border border-background-200 flex items-center justify-center text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-background-100 border border-background-200 rounded-lg py-16 text-center">
              <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <p className="font-heading font-bold text-foreground-800 mb-1">暂无相关新闻</p>
              <p className="text-sm text-foreground-500 mb-5">
                换个关键词或分类试试，也可以返回首页看看别的内容
              </p>
              <Link
                to="/"
                className="inline-block px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-background-100 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
              热门资讯
            </h3>
            <ul className="space-y-4">
              {[...newsList]
                .sort((a, b) => b.views - a.views)
                .slice(0, 5)
                .map((item, i) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="flex gap-3 group cursor-pointer">
                      <span
                        className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                          i < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-300 text-foreground-600"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
              分类浏览
            </h3>
            <div className="flex flex-wrap gap-2">
              {categories.slice(1).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => updateParam("category", cat)}
                  className="px-3 py-1.5 rounded-full bg-background-50 border border-accent-200 text-sm text-foreground-700 hover:border-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}